import React, { useEffect, useMemo, useState } from 'react';

import DoubleScrollbar from 'react-double-scrollbar';
/* Libs. */
import { usePagination, useSortBy, useTable } from 'react-table';
import { toast } from 'react-toastify';

import { TABLE_PAGE_SIZE } from 'general/constants';

import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
/* Material UI */
import RefreshIcon from '@material-ui/icons/Refresh';

/* Components */
import {
  AutoCompleteCell,
  CustomCell,
  DateCell,
  DateInput,
  EyeCell,
  EyeInput,
  Input,
  InputCell,
  SelectCell,
  SelectProductTypeCell,
  SwitchCell,
  SwitchInput,
  ThumbnailCell,
} from '.';
import NibText from '../NibText/NibText';
import Pagination from './common/Pagination';
import TBody from './common/TBody';
import THeader from './common/THeader';
import TWrapper from './common/TWrapper';
/* Styles */
import css from './nibTable.module.sass';

/* On change, also change var in  ./nibTable.module.sass*/
const ROW_HEIGHT = 55;

const NibTable = ({
  perPage = TABLE_PAGE_SIZE,
  columns = [],
  data = [],
  update = () => {},
  end = true,
  onEdit,
  isLoading = false,
  onAdd,
  onRemove,
  refresh = false,
  infi = false,
  showTableFooter = true,
  customOnAdd = false,
  customOnEdit = false,
  customEditIcon = false,
  readOnly = false,
  filters = [],
  search = false,
  onSearch = () => {},
  onFilter = () => {},
  disableAddButton = false,
  ...rest
}) => {
  let [rowToEdit, setRowToEdit] = useState({});
  let [isAdd, setAdd] = useState(false);
  let [addRow, setAddRow] = useState({});
  let [editionData, setEditionData] = useState({});
  let [forceUpdateRow, setRowToForceUpdate] = useState({});
  let [touchedFields, setTouchedFields] = useState([]);

  const getComponent = (type, isAdd = false, data = {}) => {
    switch (type) {
      case 'text':
        return !isAdd ? (props) => <InputCell {...props} data={data} /> : (props) => <Input {...props} />;
      case 'date':
        return !isAdd ? (props) => <DateCell {...props} data={data} /> : (props) => <DateInput {...props} />;
      case 'switch':
        return !isAdd ? (props) => <SwitchCell {...props} /> : (props) => <SwitchInput {...props} />;
      case 'eyeBtn':
        return !isAdd ? (props) => <EyeCell {...props} data={data} /> : (props) => <EyeInput {...props} />;
      case 'select':
        return !isAdd ? (props) => <SelectCell {...props} data={data} /> : (props) => <SelectCell {...props} />;
      case 'selectProductType':
        return !isAdd
          ? (props) => <SelectProductTypeCell {...props} data={data} />
          : (props) => <SelectProductTypeCell {...props} />;
      case 'autocomplete':
        return !isAdd
          ? (props) => <AutoCompleteCell {...props} data={data} />
          : (props) => <AutoCompleteCell {...props} />;
      case 'autocomplete.multiple':
        return (props) => {
          const extendedProps = { ...props, multiple: true };
          return <AutoCompleteCell {...extendedProps} {...data} />;
        };
      case 'thumbnail':
        return (props) => <ThumbnailCell {...props} data={data} />;
      case 'custom':
        return (props) => <CustomCell {...props} data={data} />;
      case 'readonly':
        return (props) => <InputCell {...props} data={{ ...data, readOnly: true }} />;
      default:
        return !isAdd ? (props) => <InputCell {...props} data={data} /> : (props) => <Input {...props} />;
    }
  };

  const genereteHeader = (cols) => {
    let payload = [];
    for (const col of cols) {
      let output = {
        ...col,
        Cell: col.Cell ? col.Cell : getComponent(col.type, false, col.data),
      };
      delete output.type;
      payload.push(output);
    }
    return payload;
  };

  const generateSchema = (cols) => {
    let schema = {};
    for (const col of cols) {
      let defVal = '';
      if (col.type === 'date') defVal = new Date();
      else if (col.type === 'text') defVal = '';
      else if (col.type === 'switch' || col.type === 'eyeToggle') defVal = false;
      schema[col.accessor] = defVal;
    }
    return schema;
  };

  const onAddRowInputChange = ({ accessor, value }) => {
    setAddRow({
      ...addRow,
      [accessor]: value,
    });
  };

  const generateAddRow = () => {
    let payload = [];
    for (const col of columns) {
      payload.push(
        <th>
          {getComponent(
            col.type,
            true,
          )({
            onChange: onAddRowInputChange,
            accessor: col.accessor,
          })}
        </th>,
      );
    }
    return payload;
  };

  const updateMyData = ({ accessor, value }) => {
    setTouchedFields([...touchedFields, accessor]);
    setEditionData({
      ...editionData,
      [accessor]: value,
    });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,

    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: useMemo(() => genereteHeader(columns), [columns]),
      data: useMemo(() => data, [data]),
      //   data,
      updateMyData,
      rowToEdit,
      forceUpdateRow,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (pageSize !== perPage) {
      setPageSize(perPage);
    }
  }, [perPage]);

  const addItem = () => {
    setAdd(true);
  };

  const onEditSave = async () => {
    if (touchedFields.length <= 0) {
      setEditionData({});
      setTouchedFields([]);
      setRowToEdit({ index: null, status: false });
      return;
    }
    let status = await onEdit(editionData);
    let row = rows.filter((row) => row.index === rowToEdit.index)[0];
    let { original } = row;
    if (status.error && status.cells.length >= 1) {
      /* let toUpdate = { index: row.index };
			for (const cell of status.cells) {
				toUpdate[cell] = original[cell];
			}
			setRowToForceUpdate(toUpdate); */
    } else {
      setRowToForceUpdate(original);
      setEditionData({});
      setRowToEdit({ index: null, status: false });
    }
  };

  const onNewSave = () => {
    let payload = {
      ...generateSchema(columns),
      ...addRow,
    };
    onAdd(payload);
    setAdd(false);
    toast.success('New row was successfully added!');
  };

  const editRow = (row) => {
    if (customOnEdit) {
      customOnEdit(row);
      return;
    }
    let index = row.index;
    let original = row.original;
    if (index !== rowToEdit.index) {
      setRowToEdit({ index: null, status: false });
      setEditionData({});
      setRowToEdit({ index, status: true });
      setEditionData({ id: original.id });
    } else {
      setRowToEdit({ index, status: !rowToEdit.status });
      // setEditionData({});
      setEditionData({ id: original.id });
    }
  };

  const removeRow = async (row) => {
    let status = await onRemove(row.original);
  };

  const searchFilterTypeName = 'search';
  const nonSearchFilters = filters.filter((component) => component.props.type !== searchFilterTypeName);
  const searchFilter = filters.find((component) => component.props.type === searchFilterTypeName);

  return (
    <div>
      <div className={css.wrap}>
        {!!filters.length && (
          <div className={css.toolbar}>
            <div className={css.filtersWrapper}>
              {nonSearchFilters.map((filter, i) =>
                React.cloneElement(filter, {
                  ...filter.props,
                  onFilter,
                  key: 'filter_' + i,
                  className: css.field,
                }),
              )}
              {nonSearchFilters.some(
                (f) =>
                  !!(typeof f.props.value === 'object'
                    ? Object.values(f.props.value || {}).some((v) => !!v)
                    : f.props.value),
              ) && (
                <Button onClick={() => onFilter('reset')} style={{ height: 40 }}>
                  <RefreshIcon />
                  <NibText
                    type={'light'}
                    size={'smallMedium'}
                    align={'right'}
                    style={{ padding: '0 10px' }}
                    className={'text-overflow-ellipsis'}
                  >
                    Reset
                  </NibText>
                </Button>
              )}
            </div>
            {searchFilter &&
              React.cloneElement(searchFilter, {
                ...searchFilter.props,
                onFilter,
                className: css.field,
              })}
          </div>
        )}
        <LinearProgress size={70} color="primary" style={{ visibility: isLoading ? 'visible' : 'hidden' }} />
        <TWrapper
          dataLength={rows.length}
          next={update}
          hasMore={!end}
          infi={infi}
          showTableFooter={showTableFooter}
          scrollableTargetId={rest.scrollableTargetId}
        >
          <DoubleScrollbar>
            <table className={css.table} {...getTableProps()}>
              <THeader
                headerGroups={headerGroups}
                isAdd={isAdd}
                setAdd={setAdd}
                generateAddRow={generateAddRow}
                onNewSave={onNewSave}
                addItem={customOnAdd ? customOnAdd : addItem}
                readOnly={readOnly}
                disableAddButton={disableAddButton}
              />

              <TBody
                customEditIcon={customEditIcon}
                getTableBodyProps={getTableBodyProps}
                rowToEdit={rowToEdit}
                prepareRow={prepareRow}
                removeRow={removeRow}
                rows={infi ? rows : page}
                onEditSave={onEditSave}
                editRow={editRow}
                readOnly={readOnly}
              />
            </table>
          </DoubleScrollbar>
          {!infi ? (
            <Pagination
              {...{
                canPreviousPage,
                canNextPage,
                pageOptions,
                pageCount,
                gotoPage,
                nextPage,
                previousPage,
                setPageSize,
                pageIndex,
                pageSize,
              }}
            />
          ) : null}
        </TWrapper>
      </div>
    </div>
  );
};

export default NibTable;
